'use client'

import Link from 'next/link'
import { usePathname } from 'next/navigation'
import { Menu, X } from 'lucide-react'
import { useState, useRef, useEffect, useCallback } from 'react'
import Image from 'next/image'
import { useFocusTrap } from '@/lib/useFocusTrap'

const navigation = [
  { name: 'Aircraft Leasing', href: '/aircraft-leasing' },
  { name: 'MRO Services', href: '/mro' },
  { name: 'Aircraft & Parts Listings', href: '/for-sale' },
  { name: 'About', href: '/about' },
  { name: 'Contact', href: '/contact' }
]

export default function Header() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  const [isClosing, setIsClosing] = useState(false)
  const [scrollY, setScrollY] = useState(0)
  const pathname = usePathname()
  const mobileMenuRef = useRef<HTMLDivElement>(null)
  const hamburgerButtonRef = useRef<HTMLButtonElement>(null)
  const firstFocusableRef = useRef<HTMLAnchorElement>(null)
  const rafRef = useRef<number | undefined>(undefined)
  const lastScrollY = useRef(0)
  const headerBoxRef = useRef<HTMLDivElement>(null)
  const [swipeX, setSwipeX] = useState<number | null>(null)

  // Mobile-optimized scroll handler with throttling and reduced updates
  useEffect(() => {
    let ticking = false
    
    const handleScroll = () => {
      if (!ticking) {
        requestAnimationFrame(() => {
          const currentScrollY = window.scrollY
          // Only update if scroll difference is significant (reduces mobile repaints)
          if (Math.abs(currentScrollY - lastScrollY.current) > 5) {
            setScrollY(currentScrollY)
            lastScrollY.current = currentScrollY
          }
          ticking = false
        })
        ticking = true
      }
    }

    // Use passive listener for better mobile performance
    window.addEventListener('scroll', handleScroll, { passive: true })
    return () => {
      window.removeEventListener('scroll', handleScroll)
    }
  }, [])

  // Calculate scroll-based values for smooth transitions (translucent at top → solid on scroll)
  const scrollProgress = Math.min(scrollY / 120, 1) // Normalize to 0-1 over ~120px
  const baseOpacity = 0.25
  const maxOpacity = 0.9
  const backgroundOpacity = baseOpacity + (maxOpacity - baseOpacity) * scrollProgress
  const baseBlur = 8
  const maxBlur = 16
  const backdropBlur = baseBlur + (maxBlur - baseBlur) * scrollProgress
  const isScrolled = scrollY > 10

  // Close mobile menu handler with smooth animation
  const closeMobileMenu = useCallback(() => {
    setIsClosing(true)
    // Wait for slide-out animation before removing from DOM
    setTimeout(() => {
      setMobileMenuOpen(false)
      setIsClosing(false)
      setSwipeX(null)
      // Return focus to hamburger button
      setTimeout(() => {
        hamburgerButtonRef.current?.focus()
      }, 50)
    }, 250) // Match animation duration
  }, [])

  // Handle body scroll lock and keyboard events
  useEffect(() => {
    if (mobileMenuOpen) {
      // Prevent body scroll when mobile menu is open
      document.body.style.overflow = 'hidden'
      document.body.style.position = 'fixed'
      document.body.style.width = '100%'
      document.body.style.top = `-${window.scrollY}px`
      document.body.style.overscrollBehaviorY = 'contain'

      // Move focus to first focusable element
      setTimeout(() => {
        firstFocusableRef.current?.focus()
      }, 100)

      // Handle Escape key
      const handleEscape = (e: KeyboardEvent) => {
        if (e.key === 'Escape') {
          closeMobileMenu()
        }
      }
      document.addEventListener('keydown', handleEscape)

      // Inert background regions for a11y (with Safari fallback)
      const mainEl = document.querySelector('main#main-content') as HTMLElement | null
      const footerEl = document.querySelector('footer') as HTMLElement | null
      const setInert = (el: HTMLElement | null, inert: boolean) => {
        if (!el) return
        try {
          ;(el as any).inert = inert
        } catch {
          // noop if not supported
        }
        if (inert) {
          el.setAttribute('aria-hidden', 'true')
        } else {
          el.removeAttribute('aria-hidden')
        }
      }
      setInert(mainEl, true)
      setInert(footerEl, true)

      return () => {
        document.removeEventListener('keydown', handleEscape)
        // Restore scroll position
        const scrollY = document.body.style.top
        document.body.style.overflow = ''
        document.body.style.position = ''
        document.body.style.width = ''
        document.body.style.top = ''
        document.body.style.overscrollBehaviorY = ''
        window.scrollTo(0, parseInt(scrollY || '0') * -1)
        setInert(mainEl, false)
        setInert(footerEl, false)
      }
    }
  }, [mobileMenuOpen, closeMobileMenu])

  // Close mobile menu on route change
  useEffect(() => {
    setMobileMenuOpen(false)
  }, [pathname])

  const isActive = (href: string) => {
    // Don't highlight any nav item when on home page
    if (pathname === '/') {
      return false
    }
    // For other pages, check if the current path starts with the nav href
    return pathname.startsWith(href)
  }

  // Focus trap within the mobile menu when open
  useFocusTrap(mobileMenuRef as any, mobileMenuOpen)

  // Interactive swipe gesture (drawer follows finger)
  useEffect(() => {
    if (!mobileMenuOpen) return
    const el = mobileMenuRef.current
    if (!el) return
    let startX = 0
    let currentX = 0
    let isTouching = false

    const onStart = (e: TouchEvent) => {
      if (e.touches.length !== 1) return
      isTouching = true
      startX = e.touches[0].clientX
      currentX = startX
      // Only allow swipe if starting from the drawer
      const drawerWidth = el.offsetWidth
      if (startX > drawerWidth) {
        isTouching = false
        return
      }
    }

    const onMove = (e: TouchEvent) => {
      if (!isTouching) return
      currentX = e.touches[0].clientX
      const deltaX = currentX - startX
      
      // Only allow swiping left (negative delta)
      if (deltaX < 0) {
        const drawerWidth = el.offsetWidth
        const swipeProgress = Math.max(0, Math.min(1, Math.abs(deltaX) / drawerWidth))
        setSwipeX(deltaX)
        
        // Apply transform in real-time for smooth following
        requestAnimationFrame(() => {
          if (el) {
            el.style.transform = `translateX(${Math.min(0, deltaX)}px)`
          }
        })
      }
    }

    const onEnd = () => {
      if (!isTouching) return
      isTouching = false
      const deltaX = currentX - startX
      
      // Reset transform
      if (el) {
        el.style.transform = ''
      }
      
      setSwipeX(null)
      
      // Close if swiped left more than 80px or 25% of drawer width
      const drawerWidth = el.offsetWidth
      const threshold = Math.min(80, drawerWidth * 0.25)
      
      if (deltaX < -threshold) {
        closeMobileMenu()
      }
    }

    el.addEventListener('touchstart', onStart, { passive: true })
    el.addEventListener('touchmove', onMove, { passive: true })
    el.addEventListener('touchend', onEnd, { passive: true })
    el.addEventListener('touchcancel', onEnd, { passive: true })
    
    return () => {
      el.removeEventListener('touchstart', onStart)
      el.removeEventListener('touchmove', onMove)
      el.removeEventListener('touchend', onEnd)
      el.removeEventListener('touchcancel', onEnd)
    }
  }, [mobileMenuOpen, closeMobileMenu])


  return (
    <header 
      className="fixed top-0 left-0 right-0 z-50 transition-all duration-300 ease-out"
      style={{
        paddingTop: 'env(safe-area-inset-top)', // Handle iOS notch
        paddingLeft: 'env(safe-area-inset-left)',
        paddingRight: 'env(safe-area-inset-right)',
      }}
      role="banner"
    >
      {/* Full-width header bar (non-floating) */}
      <div 
        ref={headerBoxRef}
        className={`transition-all duration-300 ease-out ${isScrolled ? 'shadow-lg' : ''}`}
        style={{
          backgroundColor: `rgba(30, 41, 59, ${backgroundOpacity})`, // slate-800 with dynamic opacity
          backdropFilter: `blur(${backdropBlur}px)`,
          WebkitBackdropFilter: `blur(${backdropBlur}px)`, // Safari support
          borderBottom: isScrolled ? '1px solid rgba(255,255,255,0.08)' : '1px solid rgba(255,255,255,0.06)'
        }}
      >
        <div className="mx-auto max-w-7xl px-4 xl:px-6">
          <div className="flex items-center justify-between h-16 sm:h-18 md:h-20 lg:h-22">
          {/* Logo */}
          <div className="flex-shrink-0">
            <Link 
              href="/" 
              className="flex items-center focus:outline-none rounded-md focus-visible:ring-2 focus-visible:ring-blue-400 focus-visible:ring-offset-2 focus-visible:ring-offset-slate-800"
              aria-label="Spitzer Helicopters - Return to homepage"
            >
              <span className="sr-only">Spitzer Helicopters</span>
              <div className="relative overflow-hidden h-7 w-[140px] sm:h-9 sm:w-[170px] md:h-11 md:w-[200px] lg:h-13 lg:w-[240px]">
                <Image
                  src="/images/models/Logos/Spitzer White.webp"
                  alt="Spitzer Helicopter Leasing - Professional Aircraft Leasing Services"
                  fill
                  priority
                  sizes="(min-width: 1280px) 240px, (min-width: 768px) 200px, (min-width: 640px) 170px, 140px"
                  className="object-contain object-left"
                  quality={95}
                />
              </div>
            </Link>
          </div>

          {/* Desktop Navigation */}
          <nav className="hidden xl:flex items-center justify-end gap-3" role="navigation" aria-label="Main navigation">
            {navigation.map((item) => (
              <Link
                key={item.name}
                href={item.href}
                prefetch={false}
                className={`px-5 py-2.5 text-sm font-semibold rounded-full transition-all duration-200 whitespace-nowrap focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-300 focus-visible:ring-offset-2 focus-visible:ring-offset-slate-800 ${
                  isActive(item.href)
                    ? 'bg-white text-slate-900 shadow-sm'
                    : 'text-slate-100 hover:text-white hover:bg-white/10'
                }`}
                aria-current={isActive(item.href) ? 'page' : undefined}
              >
                {item.name}
              </Link>
            ))}
          </nav>


          {/* Mobile menu button */}
          <div className="xl:hidden">
            <button
              ref={hamburgerButtonRef}
              type="button"
              className="inline-flex items-center justify-center p-2.5 rounded-lg text-gray-100 hover:text-white hover:bg-slate-700/50 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-400 transition-colors duration-200 touch-manipulation active:scale-95"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              aria-expanded={mobileMenuOpen}
              aria-controls="mobile-menu"
              aria-label={mobileMenuOpen ? 'Close navigation menu' : 'Open navigation menu'}
            >
              <span className="sr-only">{mobileMenuOpen ? 'Close' : 'Open'} main menu</span>
              <Menu className="block h-6 w-6" aria-hidden="true" />
            </button>
          </div>
        </div>
      </div>
      </div>
      {/* End Header Bar */}

      {/* Mobile Navigation Drawer - Sliding from Left */}
      {mobileMenuOpen && (
        <>
          {/* Backdrop Overlay */}
          <div
            className={`xl:hidden fixed inset-0 z-[60] bg-black/60 backdrop-blur-sm mobile-menu-animate ${
              isClosing ? 'animate-out fade-out duration-250' : 'animate-in fade-in duration-350'
            }`}
            onClick={closeMobileMenu}
            aria-hidden="true"
            style={{ 
              height: '100dvh',
              touchAction: 'none',
              transition: 'opacity 0.3s cubic-bezier(0.16, 1, 0.3, 1)',
            }}
          />

          {/* Sliding Drawer Panel */}
          <div
            ref={mobileMenuRef}
            id="mobile-menu"
            role="dialog"
            aria-modal="true"
            aria-label="Mobile navigation drawer"
            className={`xl:hidden fixed top-0 left-0 bottom-0 z-[70] w-[280px] sm:w-[320px] bg-white shadow-2xl mobile-menu-animate ${
              isClosing ? 'drawer-slide-out' : 'drawer-slide-in'
            }`}
            style={{
              height: '100dvh',
              overscrollBehaviorY: 'contain',
              WebkitOverflowScrolling: 'touch',
              willChange: 'transform',
              paddingTop: 'env(safe-area-inset-top)',
              paddingBottom: 'env(safe-area-inset-bottom)',
              paddingLeft: 'env(safe-area-inset-left)',
            }}
          >
            {/* Drawer Content Container */}
            <div className="h-full flex flex-col overflow-hidden">
              
              {/* Header Section */}
              <div className="flex-shrink-0 px-6 py-5 border-b border-gray-100">
                <div className="flex items-center justify-between">
                  {/* Logo */}
                  <Link 
                    href="/" 
                    className="focus:outline-none rounded-lg focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 transition-transform active:scale-95"
                    aria-label="Spitzer Helicopters - Return to homepage"
                    onClick={closeMobileMenu}
                  >
                    <span className="sr-only">Spitzer Helicopters</span>
                    <div className="relative h-8 w-[150px]">
                      <Image
                        src="/images/models/Logos/Spitzer White.webp"
                        alt="Spitzer Helicopter Leasing"
                        fill
                        priority
                        sizes="150px"
                        className="object-contain object-left brightness-0"
                        quality={95}
                        style={{ filter: 'brightness(0) saturate(100%)' }}
                      />
                    </div>
                  </Link>

                  {/* Close Button */}
                  <button
                    type="button"
                    onClick={closeMobileMenu}
                    className="inline-flex items-center justify-center min-w-[44px] min-h-[44px] -mr-2 text-slate-600 hover:text-slate-900 hover:bg-slate-100 rounded-lg focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 transition-all duration-200 touch-manipulation active:scale-90"
                    aria-label="Close navigation menu"
                  >
                    <X className="h-6 w-6" aria-hidden="true" strokeWidth={2} />
                    <span className="sr-only">Close menu</span>
                  </button>
                </div>
              </div>

              {/* Navigation Links - Scrollable */}
              <nav 
                className="flex-1 overflow-y-auto overscroll-contain px-4 py-6" 
                role="navigation" 
                aria-label="Main navigation"
              >
                <ul className="space-y-1">
                  {navigation.map((item, index) => (
                    <li key={item.name}>
                      <Link
                        ref={index === 0 ? firstFocusableRef : undefined}
                        href={item.href}
                        prefetch={false}
                        onClick={closeMobileMenu}
                        className={`group flex items-center justify-between w-full px-4 py-3.5 text-[15px] font-semibold rounded-xl transition-all duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 touch-manipulation active:scale-[0.97] ${
                          isActive(item.href)
                            ? 'bg-blue-50 text-blue-700 shadow-sm'
                            : 'text-slate-700 hover:bg-slate-50 hover:text-slate-900'
                        }`}
                        style={{
                          minHeight: '52px',
                        }}
                        aria-current={isActive(item.href) ? 'page' : undefined}
                      >
                        <span className="flex items-center gap-3">
                          {/* Active Indicator Dot */}
                          {isActive(item.href) && (
                            <span className="w-1.5 h-1.5 bg-blue-600 rounded-full" aria-hidden="true"></span>
                          )}
                          <span>{item.name}</span>
                        </span>
                        
                        {/* Arrow Icon */}
                        <svg 
                          className={`w-5 h-5 transition-transform duration-200 ${
                            isActive(item.href) 
                              ? 'text-blue-600' 
                              : 'text-slate-400 group-hover:text-slate-600 group-hover:translate-x-0.5'
                          }`}
                          fill="none" 
                          viewBox="0 0 24 24" 
                          stroke="currentColor"
                          aria-hidden="true"
                        >
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                      </Link>
                    </li>
                  ))}
                </ul>
              </nav>

            </div>
          </div>
        </>
      )}
      
    </header>
  )
}
